const API_BASE_URL = 'https://api.aidify.pk/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  // Set auth token in localStorage
  setAuthToken(token) {
    localStorage.setItem('authToken', token);
  }

  // Remove auth token from localStorage
  removeAuthToken() {
    localStorage.removeItem('authToken');
  }

  // Get user data from localStorage
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Set user data in localStorage
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  // Remove user data from localStorage
  removeUser() {
    localStorage.removeItem('user');
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();

    const config = {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    // Only set default Content-Type if not FormData and no custom Content-Type is provided
    if (!(options.body instanceof FormData) && !options.headers?.['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Authentication methods
  async login(email, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data.token) {
      this.setAuthToken(response.data.token);
      this.setUser(response.data);
    }

    return response;
  }

  async register(userData) {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.success && response.data.token) {
      this.setAuthToken(response.data.token);
      this.setUser(response.data);
    }

    return response;
  }

  async loginSuperAdmin(email, password) {
    const response = await this.request('/auth/login-super-admin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data.token) {
      this.setAuthToken(response.data.token);
      this.setUser(response.data);
    }

    return response;
  }

  async registerSuperAdmin(userData) {
    const response = await this.request('/auth/register-super-admin', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.success && response.data.token) {
      this.setAuthToken(response.data.token);
      this.setUser(response.data);
    }

    return response;
  }

  async getCurrentUser() {
    return await this.request('/auth/me');
  }

  async updateProfile(profileData) {
    return await this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return await this.request('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Patient methods
  async getPatients(page = 1, limit = 10, search = '') {
    const params = new URLSearchParams({
      page,
      limit,
      ...(search && { search }),
    });
    return await this.request(`/patients?${params}`);
  }

  async createPatient(patientData) {
    return await this.request('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  }

  async getPatient(id) {
    return await this.request(`/patients/${id}`);
  }

  async updatePatient(id, patientData) {
    return await this.request(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    });
  }

  async getPatientHealthMetrics(id) {
    return await this.request(`/patients/${id}/health-metrics`);
  }

  async addHealthMetric(id, metricData) {
    return await this.request(`/patients/${id}/health-metrics`, {
      method: 'POST',
      body: JSON.stringify(metricData),
    });
  }

  // Appointment methods
  async getAppointments(page = 1, limit = 10, status = '', date = '') {
    const params = new URLSearchParams({
      page,
      limit,
      ...(status && { status }),
      ...(date && { date }),
    });
    return await this.request(`/appointments?${params}`);
  }

  async getAppointment(id) {
    return await this.request(`/appointments/${id}`);
  }

  async createAppointment(appointmentData) {
    return await this.request('/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async updateAppointment(id, appointmentData) {
    return await this.request(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(appointmentData),
    });
  }

  async deleteAppointment(id) {
    return await this.request(`/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  async getAppointmentStats() {
    return await this.request('/appointments/stats');
  }

  // Doctor methods
  async getDoctors(page = 1, limit = 10, specialty = '', search = '') {
    const params = new URLSearchParams({
      page,
      limit,
      ...(specialty && { specialty }),
      ...(search && { search }),
    });
    return await this.request(`/doctors?${params}`);
  }

  async getDoctor(id) {
    return await this.request(`/doctors/${id}`);
  }

  async updateDoctor(id, doctorData) {
    return await this.request(`/doctors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(doctorData),
    });
  }

  async getDoctorSchedule(id, date = '') {
    const params = new URLSearchParams({
      ...(date && { date }),
    });
    return await this.request(`/doctors/${id}/schedule?${params}`);
  }

  async getDoctorDashboard(id) {
    return await this.request(`/doctors/${id}/dashboard`);
  }

  async getAvailableSlots(id, date) {
    return await this.request(`/doctors/${id}/available-slots?date=${date}`);
  }

  async getDoctorSpecialties() {
    return await this.request('/doctors/specialties');
  }

  // Driver methods
  async registerDriver(driverData) {
    // Check if driverData is FormData (for file uploads) or regular object
    const isFormData = driverData instanceof FormData;
    
    const options = {
      method: 'POST',
    };
    
    if (isFormData) {
      // For file uploads, don't set Content-Type header (let browser set it with boundary)
      options.body = driverData;
    } else {
      // For JSON data
      options.body = JSON.stringify(driverData);
      options.headers = {
        'Content-Type': 'application/json',
      };
    }
    
    return await this.request('/drivers/register', options);
  }

  async getDrivers(page = 1, limit = 10, status = '', search = '') {
    const params = new URLSearchParams({
      page,
      limit,
      ...(status && { status }),
      ...(search && { search }),
    });
    return await this.request(`/drivers?${params}`);
  }

  async getDriver(id) {
    return await this.request(`/drivers/${id}`);
  }

  async updateDriver(id, driverData) {
    return await this.request(`/drivers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(driverData),
    });
  }

  async updateDriverStatus(id, status) {
    return await this.request(`/drivers/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async getAvailableDrivers(location = '', vehicleType = '') {
    const params = new URLSearchParams({
      ...(location && { location }),
      ...(vehicleType && { vehicleType }),
    });
    return await this.request(`/drivers/available?${params}`);
  }

  // Admin methods
  async getAdminDashboard() {
    return await this.request('/admin/dashboard');
  }

  async getAdminStats() {
    return await this.request('/admin/stats');
  }

  // Super Admin methods
  async getSuperAdminDashboard() {
    return await this.request('/admin/super-dashboard');
  }

  async getSystemOverview() {
    return await this.request('/admin/super-admin/system-overview');
  }

  async getRoleManagement() {
    return await this.request('/admin/super-admin/roles');
  }

  async updateUserRole(id, role) {
    return await this.request(`/admin/super-admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async getUsers(page = 1, limit = 10, role = '', search = '') {
    const params = new URLSearchParams({
      page,
      limit,
      ...(role && { role }),
      ...(search && { search }),
    });
    return await this.request(`/admin/users?${params}`);
  }

  async updateUserRole(id, role) {
    return await this.request(`/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async deleteUser(id) {
    return await this.request(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  async getSystemLogs(page = 1, limit = 10) {
    const params = new URLSearchParams({
      page,
      limit,
    });
    return await this.request(`/admin/logs?${params}`);
  }

  // Notification methods
  async getNotifications(page = 1, limit = 10, unread = false) {
    const params = new URLSearchParams({
      page,
      limit,
      ...(unread && { unread: 'true' }),
    });
    return await this.request(`/notifications?${params}`);
  }

  async getNotification(id) {
    return await this.request(`/notifications/${id}`);
  }

  async markNotificationAsRead(id) {
    return await this.request(`/notifications/${id}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsAsRead() {
    return await this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }

  async deleteNotification(id) {
    return await this.request(`/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  async getNotificationCount() {
    return await this.request('/notifications/count');
  }

  async createNotification(notificationData) {
    return await this.request('/notifications', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  }

  async createBulkNotifications(notificationData) {
    return await this.request('/notifications/bulk', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  }

  // Logout
  logout() {
    this.removeAuthToken();
    this.removeUser();
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getAuthToken();
  }

  // Check if user has specific role
  hasRole(role) {
    const user = this.getUser();
    return user && user.role === role;
  }
}

export default new ApiService(); 
